import { Trans, useTranslation } from 'next-i18next';
import { CheckCircleFilled } from '@ant-design/icons';
import { Divider } from 'antd';
import Image from 'next/legacy/image';

import { BANNER_CERTIFIED } from '@source/pages/CarroCertified/constants';
import useWindowResize from '@source/hooks/useWindowResize';
import useRegionConfig from '@source/hooks/useRegionConfig';
import { TRegionKey } from '@source/interface';

import i18n from '@source/i18next';
import {
  StyledButton,
  StyledCertified,
  StyledDesktopWrapper,
  StyledDividerTitle,
  StyledList,
  StyledWrapper,
  StyledHeadingMobile,
} from './CCPOCertified.style';

import SeeMoreIcon from '../../assets/icons/see-more.svg';

export function CCPOCertifiedMobile({
  region,
  open,
  setOpen,
}: {
  region: TRegionKey;
  open: boolean;
  setOpen: (value: boolean) => void;
}) {
  const { t } = useTranslation('ccpo');

  return (
    <StyledWrapper role="region" aria-label="Carro Certified Program">
      <StyledCertified className={open ? 'extend-height' : ''} role="article">
        <StyledDividerTitle>
          <StyledHeadingMobile>
            <Trans
              ns="ccpo"
              defaults={region === 'my' ? 'ccpo.banner.certified.titleMobileMY' : 'ccpo.banner.certified.titleMobile'}
              components={{ span: <span />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
            />
          </StyledHeadingMobile>
        </StyledDividerTitle>
        <StyledList role="list" aria-label="Certification features">
          {BANNER_CERTIFIED(region).map((item, index) => {
            const content = region === 'hk' && index === 1 ? 'ccpo.banner.certified.cert2HK' : item.content;

            return (
              <li key={index}>
                <CheckCircleFilled style={{ color: '#FFA680' }} aria-hidden="true" />
                <span>
                  <Trans
                    ns="ccpo"
                    defaults={content}
                    components={{
                      br: <> </>,
                      span: <span />,
                      specificFont: <span style={{ fontFamily: 'Poppins' }} />,
                    }}
                  />
                </span>
              </li>
            );
          })}
        </StyledList>
        <StyledButton
          className="see-more-btn"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={t(`ccpo.banner.${open ? 'seeLess' : 'seeMore'}`)}
        >
          {t(`ccpo.banner.${open ? 'seeLess' : 'seeMore'}`)}
          {open ? (
            <Image
              className="see-less-icon"
              src={SeeMoreIcon}
              alt="See less icon"
              width={12}
              height={12}
              layout="fixed"
              aria-hidden="true"
            />
          ) : (
            <Image src={SeeMoreIcon} alt="See more icon" width={12} height={12} layout="fixed" aria-hidden="true" />
          )}
        </StyledButton>
      </StyledCertified>
    </StyledWrapper>
  );
}

export default function CCPOCertified({
  region,
  open,
  setOpen,
}: {
  region: TRegionKey;
  open: boolean;
  setOpen: (value: boolean) => void;
}) {
  const { t } = useTranslation('ccpo');
  const {
    countryConfig: { country },
  } = useRegionConfig();

  const [width] = useWindowResize();
  if (width < 576) {
    return (
      <StyledWrapper role="region" aria-label="Carro Certified Program">
        <CCPOCertifiedMobile region={region} open={open} setOpen={setOpen} />
      </StyledWrapper>
    );
  }

  const isV2 = country ? ['my', 'jp', 'hk'].includes(country) : false;

  // Desktop View
  return (
    <StyledWrapper role="region" aria-label="Carro Certified Program">
      <StyledDesktopWrapper country={country ?? 'sg'} isMS={i18n.language === 'ms-SG'} isV2={isV2}>
        <div className="item-wrapper" role="list" aria-label="Certification features">
          <h2
            className={region === 'my' ? 'item-title item-title-my' : 'item-title'}
            aria-level={2}
            style={{ margin: '0' }}
          >
            <Trans
              ns="ccpo"
              defaults={t(
                region === 'my' ? 'ccpo.banner.certified.titleDesktopMY' : 'ccpo.banner.certified.titleDesktop',
              )}
              components={{ span: <span />, br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
            />
          </h2>
          <Divider type="vertical" />

          {BANNER_CERTIFIED(region).map((item, index) => {
            const content = region === 'hk' && index === 1 ? 'ccpo.banner.certified.cert2HK' : item.content;

            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} role="listitem" className="item">
                <img src={item.img} className="item-icon" alt={`${item.content} icon`} aria-hidden="true" />
                <span className="item-content">
                  <Trans ns="ccpo" defaults={content} components={{ br: <br />, span: <span /> }} />
                </span>
                {index < BANNER_CERTIFIED(region).length - 1 && <Divider type="vertical" />}
              </div>
            );
          })}
        </div>
      </StyledDesktopWrapper>
    </StyledWrapper>
  );
}
